import {
  Button,
  colors,
  Icon,
  IconButton,
  type IconName,
  Link,
  Modal,
  Tag,
  type TagVariant,
  Tooltip,
} from '@dev-spendesk/grapes';
import classNames from 'classnames';
import { useState } from 'react';
import { Trans } from 'react-i18next';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { routeFor, routes } from 'src/core/constants/routes';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { NotificationType, useNotifications } from 'src/core/modules/app/notifications/hooks/useNotifications';
import {
  useCreateSpainClosurePeriod,
  useDownloadSpainClosurePeriod,
} from 'src/core/modules/bookkeep/apis/useSpainClosurePeriods';

import ellipseIcon from './assets/icons/ellipse.svg';
import { type SanitizedSpainClosurePeriod } from './helpers/sanitizePeriodsList';

const StatusIcon = ({
  state,
}: {
  state: SanitizedSpainClosurePeriod['state'];
}) => {
  if (state === 'completed') {
    return (
      <Icon name="circle-check" size="m" color={colors.contentSuccessDefault} />
    );
  }
  return <img src={ellipseIcon} alt="" className="w-[16px] object-contain" />;
};

const StatusTag = ({
  state,
}: {
  state: SanitizedSpainClosurePeriod['state'];
}) => {
  const { t } = useTranslation('global');
  const statusTagMap: Partial<
    Record<
      SanitizedSpainClosurePeriod['state'],
      {
        iconName?: IconName;
        variant: TagVariant;
        text: string;
        tooltipContent?: string;
      }
    >
  > = {
    completed: {
      iconName: 'lock-close',
      variant: 'success',
      text: t('bookkeep.settings.spanishDpr.periods.tags.archived'),
    },
    ongoing: {
      iconName: 'clock-filled',
      variant: 'info',
      text: t('bookkeep.settings.spanishDpr.periods.tags.archivingInProgress'),
    },
    pending: {
      iconName: 'clock-filled',
      variant: 'info',
      text: t('bookkeep.settings.spanishDpr.periods.tags.archivingInProgress'),
    },
    current: {
      variant: 'purple',
      text: t('bookkeep.settings.spanishDpr.periods.tags.currentYear'),
      tooltipContent: t(
        'bookkeep.settings.spanishDpr.periods.tags.cannotArchiveCurrentYear',
      ),
    },
  };

  const statusTag = statusTagMap[state];
  if (!statusTag) {
    return null;
  }

  if (statusTag.tooltipContent) {
    return (
      <Tooltip content={statusTag.tooltipContent}>
        <Tag
          iconName={statusTag.iconName}
          variant={statusTag.variant}
          className="cursor-pointer"
        >
          {statusTag.text}
        </Tag>
      </Tooltip>
    );
  }

  return (
    <Tag
      iconName={statusTag.iconName}
      variant={statusTag.variant}
      className="cursor-pointer"
    >
      {statusTag.text}
    </Tag>
  );
};

const ActionButton = ({
  period,
  isPending,
  setIsArchiveConfirmModalOpen,
}: {
  period: SanitizedSpainClosurePeriod;
  isPending: boolean;
  setIsArchiveConfirmModalOpen: (isOpen: boolean) => void;
}) => {
  const { t } = useTranslation('global');
  const { downloadPeriod, isPending: isDownloadPending } =
    useDownloadSpainClosurePeriod();
  const { pushNotif } = useNotifications();
  const periodId = period.id;
  if (period.state === 'completed' && periodId) {
    return (
      <Tooltip
        content={t(
          'bookkeep.settings.spanishDpr.periods.buttons.archivedDownloadTooltip',
        )}
      >
        <IconButton
          iconName="arrow-down-tray"
          variant="secondaryNeutral"
          aria-label="Download"
          onClick={() => downloadPeriod(periodId, {
            onSuccess: () => {
              pushNotif({
                type: NotificationType.Success,
                message: t('bookkeep.settings.spanishDpr.periods.success.downloadStarted'),
              });
            },
            onError: () => {
              pushNotif({
                type: NotificationType.Danger,
                message: t('bookkeep.settings.spanishDpr.periods.error.downloadFailed'),
              });
            },
          })}
          isDisabled={isDownloadPending}
        />
      </Tooltip>
    );
  }

  if (period.state === 'to_process') {
    if (period.readyToArchive) {
      return (
        <Button
          variant="secondaryNeutral"
          text={t('bookkeep.settings.spanishDpr.periods.buttons.archive')}
          onClick={() => setIsArchiveConfirmModalOpen(true)}
          isDisabled={isPending}
        />
      );
    }
    return (
      <Tooltip
        content={t(
          'bookkeep.settings.spanishDpr.periods.buttons.archiveNotAvailableTooltip',
        )}
      >
        <Button
          variant="secondaryNeutral"
          text={t('bookkeep.settings.spanishDpr.periods.buttons.archive')}
          isDisabled
        />
      </Tooltip>
    );
  }
  return null;
};

export function SpanishDprPeriod({
  period,
  className,
}: {
  className?: string;
  period: SanitizedSpainClosurePeriod;
}) {
  const { activeLanguage, t } = useTranslation('global');
  const companyId = useCompanyId();
  const formatter = new Intl.DateTimeFormat(activeLanguage, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

  const formattedFromDate = formatter.format(new Date(period.from));
  const formattedToDate = formatter.format(new Date(period.to));

  const formattedPeriodDates = `${formattedFromDate} - ${formattedToDate}`;

  const { createPeriod, isPending } = useCreateSpainClosurePeriod();
  const [isArchiveConfirmModalOpen, setIsArchiveConfirmModalOpen] =
    useState(false);
  const [isArchiveSubmittedModalOpen, setIsArchiveSubmittedModalOpen] =
    useState(false);

  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const payablesUrl = routeFor(routes.PAYABLES_ALL.path, {
    company: companyId,
  });

  const handleArchive = () => {
    const fromDate = new Date(period.from).toISOString().split('T')[0];
    const toDate = new Date(period.to).toISOString().split('T')[0];

    createPeriod(
      { from: fromDate, to: toDate },
      {
        onSuccess: () => {
          setIsArchiveConfirmModalOpen(false);
          setIsArchiveSubmittedModalOpen(true);
        },
        onError: (error: unknown) => {
          setErrorMessage((error as Error).message as string);
          setIsArchiveConfirmModalOpen(false);
        },
      },
    );
  };

  return (
    <>
      {/* Archive confirm modal */}
      <Modal
        isOpen={isArchiveConfirmModalOpen}
        onClose={() => setIsArchiveConfirmModalOpen(false)}
        title={t(
          'bookkeep.settings.spanishDpr.periods.archiveConfirmModal.title',
        )}
        iconName="triangle-warning"
        iconVariant="warning"
        actions={[
          <Button
            key="cancel"
            variant="secondaryNeutral"
            text={t('misc.cancel')}
            onClick={() => setIsArchiveConfirmModalOpen(false)}
          />,
          <Button
            key="archive"
            variant="primaryAlert"
            text={t(
              'bookkeep.settings.spanishDpr.periods.archiveConfirmModal.archiveButton',
            )}
            onClick={handleArchive}
            isDisabled={isPending}
            isLoading={isPending}
          />,
        ]}
      >
        <ol className="body-m text-content-primary-default flex list-outside flex-col items-start gap-16 px-12 py-24 text-left">
          <li className="">
            {t(
              'bookkeep.settings.spanishDpr.periods.archiveConfirmModal.content1',
              {
                from: formattedFromDate,
                to: formattedToDate,
              },
            )}
          </li>
          <li className="body-m text-content-primary-defaul">
            {t(
              'bookkeep.settings.spanishDpr.periods.archiveConfirmModal.content2',
            )}
          </li>
        </ol>
      </Modal>

      {/* Archive submitted modal */}
      <Modal
        isOpen={isArchiveSubmittedModalOpen}
        onClose={() => setIsArchiveSubmittedModalOpen(false)}
        title={t(
          'bookkeep.settings.spanishDpr.periods.archiveSubmittedModal.title',
        )}
        iconName="clock-filled"
        iconVariant="info"
        actions={[
          <Button
            key="gotIt"
            variant="primaryBrand"
            text={t(
              'bookkeep.settings.spanishDpr.periods.archiveSubmittedModal.gotItButton',
            )}
            onClick={() => setIsArchiveSubmittedModalOpen(false)}
          />,
        ]}
      >
        {t(
          'bookkeep.settings.spanishDpr.periods.archiveSubmittedModal.content',
          {
            from: formattedFromDate,
            to: formattedToDate,
          },
        )}
      </Modal>

      <div
        className={classNames(
          'align-self-stretch border-border-default bg-background-primary-default flex h-64 items-start justify-center gap-12 px-16 py-12',
          { 'bg-background-secondary-alert-default': errorMessage },
          className,
        )}
      >
        <div className="flex h-full w-full items-center justify-between">
          <div className="flex flex-col items-start gap-8">
            <div className="flex items-center gap-8">
              <StatusIcon state={period.state} />
              <p className="body-m text-content-primary-default">
                {formattedPeriodDates}
              </p>
            </div>
            {errorMessage && (
              <div className="flex items-center gap-8">
                <Icon
                  name="hexagone-cross"
                  size="m"
                  color={colors.contentAlertDefault}
                />
                <p className="body-m text-content-alert-default">
                  {errorMessage === 'UnexportedPayablesInRange' ? (
                    <Trans i18nKey="bookkeep.settings.spanishDpr.periods.error.unexportedPayablesInRange">
                      <Link
                        href={payablesUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    </Trans>
                  ) : (
                    t('bookkeep.settings.spanishDpr.periods.error.default')
                  )}
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-8">
            <StatusTag state={period.state} />
            <ActionButton
              period={period}
              setIsArchiveConfirmModalOpen={setIsArchiveConfirmModalOpen}
              isPending={isPending}
            />
          </div>
        </div>
      </div>
    </>
  );
}
